import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Person,
    Settings,
    Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/axios';

const drawerWidth = 260;

interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    permission?: string;
}

interface AdminLayoutProps {
    menuItems: MenuItem[];
}

const AdminLayout = ({ menuItems }: AdminLayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, hasPermission, hasActiveSubscription } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

    useEffect(() => {
        let objectUrl: string | null = null;
        const fetchPhoto = async () => {
            if (!user?.profile_photo) { setProfilePhoto(null); return; }
            try {
                const res = await api.get('/me/profile-photo/serve', { responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                setProfilePhoto(objectUrl);
            } catch { setProfilePhoto(null); }
        };
        fetchPhoto();
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [user?.profile_photo]);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleLogout = () => {
        handleMenuClose();
        logout();
        navigate('/login');
    };

    // Filter menu items based on permissions (for sub_admin) and subscription status
    const filteredMenuItems = menuItems.filter(item => {
        // If super admin, allow all
        if (user?.role === 'super_admin') return true;
        if (item.permission === 'dashboard.view') return true;
        // If legacy admin with no subscription, hide everything (will be on cart page)
        if (!hasActiveSubscription()) return false;

        return !item.permission || hasPermission(item.permission);
    });

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Toolbar sx={{ py: 2, justifyContent: 'center' }}>
                <Box
                    component="img"
                    src="/leader360-logo.png"
                    alt="Leader360"
                    sx={{ height: 45, objectFit: 'contain' }}
                />
            </Toolbar>
            <Divider />
            <List sx={{ px: 1, flexGrow: 1 }}>
                {filteredMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            selected={
                                location.pathname === item.path ||
                                (item.path !== '/admin' && item.path !== '/admin/citizens' && location.pathname.startsWith(item.path + '/'))
                            }
                            onClick={() => {
                                navigate(item.path);
                                setMobileOpen(false);
                            }}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.dark' },
                                    '& .MuiListItemIcon-root': { color: 'white' },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Version 1.0.0
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: '#f9fafb' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px - 40px)` },
                    ml: { sm: `${drawerWidth + 20}px` },
                    mr: { sm: '20px' },
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: { sm: '0 0 20px 20px' },
                    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                }}
            >
                <Toolbar>
                    <IconButton
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography variant="h4" fontWeight="600" color="text.primary">
                            {user?.role === 'admin' ? '🛡️ Admin Panel' : '👤 Sub Admin Panel'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', mt: 0.5 }}>
                            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {user?.name}
                        </Typography>
                        <IconButton onClick={handleMenuOpen}>
                            <Avatar src={profilePhoto || undefined} sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                                {user?.name?.charAt(0) || 'U'}
                            </Avatar>
                        </IconButton>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <MenuItem onClick={() => { handleMenuClose(); navigate('profile'); }}>
                            <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                            Profile
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            borderRadius: '0 20px 20px 0',
                            boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            borderRadius: '0 40px 40px 0',
                            boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    m: '80px 20px 50px 20px',
                    minHeight: '100vh',
                    borderRadius: '40px 40px 40px 40px',
                    boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
                    bgcolor: '#ffffff',
                    overflow: 'hidden',
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default AdminLayout;
